/**
 * Context for caching puzzle instance IDs.
 * 
 * On app load, fetches puzzle instance IDs for all games for the past 30 days.
 * This allows session creation to use cached IDs rather than making
 * individual lookups.
 * 
 * For past dates beyond 30 days, puzzle instances are fetched on-demand
 * when needed and added to the cache.
 * 
 * Usage:
 * ```javascript
 * const { getPuzzleInstanceId, fetchPuzzleInstance, isLoading } = usePuzzleInstances();
 * const puzzleInstanceId = getPuzzleInstanceId('cloze', '2025-01-15');
 * // If not in cache, fetch it:
 * const fetchedId = await fetchPuzzleInstance('cloze', '2025-01-15');
 * ```
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getPuzzleInstances } from '../api/puzzles';

const PuzzleInstancesContext = createContext(null);

/**
 * Get the date string for N days ago in YYYY-MM-DD format.
 */
function getDateNDaysAgo(n) {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return date.toISOString().split('T')[0];
}

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getToday() {
  return new Date().toISOString().split('T')[0];
}

export function PuzzleInstancesProvider({ children }) {
  // Cache: Map of "gameSlug:date" -> instance object (with puzzle_instance_id, user_session, etc.)
  // Use a ref to avoid recreating callback functions when cache updates
  const cacheRef = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  // Track ongoing fetches to avoid duplicate requests
  const fetchingRef = useRef(new Set());
  // Version counter to force re-renders when cache is updated with new instances
  const [cacheVersion, setCacheVersion] = useState(0);
  
  const { isSignedIn } = useUser();
  const { getToken, isLoaded: authLoaded } = useAuth();

  // Fetch puzzle instances on mount (wait for Clerk to load)
  useEffect(() => {
    // Wait for Clerk to finish loading
    if (!authLoaded) return;
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchInstances = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = isSignedIn ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null) : null;
        const result = await getPuzzleInstances({
          startDate: getDateNDaysAgo(30),
          endDate: getToday(),
        }, token);

        if (result.success && result.instances) {
          // Build cache from response - store full instance objects
          const newCache = {};
          for (const item of result.instances) {
            const key = `${item.game_slug}:${item.puzzle_date}`;
            newCache[key] = item;
          }
          cacheRef.current = newCache;
        } else {
          console.warn('Failed to fetch puzzle instances:', result.error);
          setError(result.error || 'Failed to fetch puzzle instances');
        }
      } catch (err) {
        console.error('Error fetching puzzle instances:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInstances();
  }, [authLoaded, isSignedIn, getToken]);

  /**
   * Get the puzzle instance ID for a game and date.
   * Returns the user_session.puzzle_instance_id if available (when user has played),
   * otherwise returns the main puzzle_instance_id.
   * Returns null if not found in cache.
   * This function has a stable reference (no dependencies).
   */
  const getPuzzleInstanceId = useCallback((gameSlug, date) => {
    const key = `${gameSlug}:${date}`;
    const instance = cacheRef.current[key];
    if (!instance) return null;
    
    // Prefer user_session.puzzle_instance_id if available (user's actual session instance)
    // This handles cases where user played an older version of the puzzle
    if (instance.user_session?.puzzle_instance_id) {
      return instance.user_session.puzzle_instance_id;
    }
    
    // Fall back to the main puzzle_instance_id (current active instance)
    return instance.puzzle_instance_id;
  }, []);

  /**
   * Check if a puzzle instance exists in the cache.
   */
  const hasPuzzleInstance = useCallback((gameSlug, date) => {
    const key = `${gameSlug}:${date}`;
    return key in cacheRef.current;
  }, []);

  /**
   * Get the full puzzle instance object for a game and date.
   * Returns null if not found in cache.
   * This function has a stable reference (no dependencies).
   */
  const getPuzzleInstance = useCallback((gameSlug, date) => {
    const key = `${gameSlug}:${date}`;
    return cacheRef.current[key] || null;
  }, []);

  /**
   * Fetch puzzle instances for a game and date range if not all in cache.
   * Adds them to the cache. Returns an object mapping dates to puzzle instance IDs.
   * 
   * @param {string} gameSlug - Game identifier (e.g., 'cloze', 'scalinata')
   * @param {string} startDate - Start date (YYYY-MM-DD, inclusive)
   * @param {string} endDate - End date (YYYY-MM-DD, inclusive)
   * @returns {Promise<Object<string, string>>} Map of date -> puzzle instance ID
   */
  const fetchPuzzleInstancesForRange = useCallback(async (gameSlug, startDate, endDate) => {
    const rangeKey = `${gameSlug}:${startDate}:${endDate}`;
    
    // Return immediately if already fetching this range
    if (fetchingRef.current.has(rangeKey)) {
      // Wait for the existing fetch to complete
      const maxWait = 5000;
      const startTime = Date.now();
      while (fetchingRef.current.has(rangeKey) && Date.now() - startTime < maxWait) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Return what we have in cache for this range
      const result = {};
      const current = new Date(startDate);
      const end = new Date(endDate);
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        const key = `${gameSlug}:${dateStr}`;
        const instance = cacheRef.current[key];
        if (instance) {
          // Prefer user_session.puzzle_instance_id if available
          result[dateStr] = instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id;
        }
        current.setDate(current.getDate() + 1);
      }
      return result;
    }
    
    // Mark as fetching
    fetchingRef.current.add(rangeKey);
    
    try {
      const token = isSignedIn ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null) : null;
      const result = await getPuzzleInstances({
        gameSlugs: [gameSlug],
        startDate: startDate,
        endDate: endDate,
      }, token);
      
      const dateMap = {};
      if (result.success && result.instances && result.instances.length > 0) {
        for (const instance of result.instances) {
          const key = `${instance.game_slug}:${instance.puzzle_date}`;
          // Store full instance object
          cacheRef.current[key] = instance;
          // Return the correct instance ID (prefer user_session if available)
          const instanceId = instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id;
          dateMap[instance.puzzle_date] = instanceId;
        }
      }
      
      return dateMap;
    } catch (err) {
      console.error(`Error fetching puzzle instances for ${gameSlug} from ${startDate} to ${endDate}:`, err);
      return {};
    } finally {
      fetchingRef.current.delete(rangeKey);
    }
  }, [isSignedIn, getToken]);

  /**
   * Fetch a puzzle instance for a specific game and date if not in cache.
   * Adds it to the cache if found.
   * 
   * @param {string} gameSlug - Game identifier (e.g., 'cloze', 'scalinata')
   * @param {string} date - Puzzle date (YYYY-MM-DD)
   * @returns {Promise<string|null>} Puzzle instance ID if found, null otherwise
   */
  const fetchPuzzleInstance = useCallback(async (gameSlug, date) => {
    const key = `${gameSlug}:${date}`;
    
    // Return immediately if already in cache
    if (key in cacheRef.current) {
      const instance = cacheRef.current[key];
      // Prefer user_session.puzzle_instance_id if available
      return instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id;
    }
    
    // Return immediately if already fetching this key
    if (fetchingRef.current.has(key)) {
      // Wait for the existing fetch to complete
      // Poll until it's done (with timeout)
      const maxWait = 5000; // 5 seconds max
      const startTime = Date.now();
      while (fetchingRef.current.has(key) && Date.now() - startTime < maxWait) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (key in cacheRef.current) {
          const instance = cacheRef.current[key];
          return instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id;
        }
      }
      // If still not in cache after waiting, return null
      const instance = cacheRef.current[key];
      return instance ? (instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id) : null;
    }
    
    // Mark as fetching
    fetchingRef.current.add(key);
    
    try {
      const token = isSignedIn ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null) : null;
      const result = await getPuzzleInstances({
        gameSlugs: [gameSlug],
        startDate: date,
        endDate: date,
      }, token);
      
      if (result.success && result.instances && result.instances.length > 0) {
        // Should only have one instance for a specific game+date
        const instance = result.instances[0];
        const instanceKey = `${instance.game_slug}:${instance.puzzle_date}`;
        // Store full instance object
        cacheRef.current[instanceKey] = instance;
        // Increment cache version to trigger re-renders in consumers
        setCacheVersion(v => v + 1);
        // Return the correct instance ID (prefer user_session if available)
        return instance.user_session?.puzzle_instance_id || instance.puzzle_instance_id;
      }
      
      // Puzzle instance doesn't exist for this game+date
      return null;
    } catch (err) {
      console.error(`Error fetching puzzle instance for ${gameSlug} on ${date}:`, err);
      return null;
    } finally {
      fetchingRef.current.delete(key);
    }
  }, [isSignedIn, getToken]);

  const value = {
    getPuzzleInstanceId,
    getPuzzleInstance,
    hasPuzzleInstance,
    fetchPuzzleInstance,
    fetchPuzzleInstancesForRange,
    isLoading,
    error,
    cacheVersion, // Exposes cache version to force re-renders when cache is updated
  };

  return (
    <PuzzleInstancesContext.Provider value={value}>
      {children}
    </PuzzleInstancesContext.Provider>
  );
}

/**
 * Hook to access the puzzle instances cache.
 */
export function usePuzzleInstances() {
  const context = useContext(PuzzleInstancesContext);
  if (!context) {
    // Return safe defaults when no provider exists (admin portal)
    return {
      getPuzzleInstanceId: () => null,
      getPuzzleInstance: () => null,
      hasPuzzleInstance: () => false,
      fetchPuzzleInstance: async () => null,
      fetchPuzzleInstancesForRange: async () => ({}),
      isLoading: false,
      error: null,
      cacheVersion: 0,
    };
  }
  return context;
}

